import { AutumnTransition, LaunchCandidate, LaunchReview, PlanningHorizon, ProposedWorklist } from './seasonalPlanning';

export interface RackVariant {
  variant_id: number;
  color: string | null;
  size: string | null;
  target: number;
  on_hand: number;
  display_qty: number;
  needed: number;
  office?: number;
  transit?: number;
  can_order?: boolean;
  trial_renewal?: { grant_round: number; sales_baseline: number };
  blocked_reason?: string;
}

export interface RackItem {
  sku: string;
  title: string;
  image_url: string | null;
  position: number;
  variants: RackVariant[];
  reason: string;
  action: string;
  cat?: string;
  price?: number;
  selection_reason?: string;
  selection_origin?: string;
  assortment_role?: 'core' | 'observation' | 'stock_rotation' | 'seasonal_launch';
  source_policy?: 'replenish_core' | 'stock_only' | 'saved_manual_purchase' | 'seasonal_trial';
  core_qualification?: NonNullable<RackItem['history']>['core_qualification'];
  history?: {
    online_u7: number | null;
    online_u14?: number | null;
    online_u30: number | null;
    online_u90: number | null;
    store_u7?: number | null;
    store_u30?: number | null;
    store_u90?: number | null;
    store_transactions30?: number | null;
    store_sale_weeks30?: number | null;
    core_qualification?: { qualified: boolean; met: string[]; missing: string[]; units30: number; transactions30: number | null; sale_weeks30: number | null; season_appropriate: boolean | null; replenishable_proven_variants: number };
    previous_year_units: number | null;
    previous_year_days: number;
    trend_label: string;
    confidence_note: string;
  };
  season_advisory?: { priority: number; reason: string; kind: string; historical_support: boolean };
  metrics: {
    display_days: number | null;
    days_since_sale: number | null;
    fitting_sessions: number | null;
    bought_sessions: number | null;
  };
}

export interface AssortmentStrategy {
  mode: 'store_core_stock_rotation';
  core_target_percent: number;
  rotation_target_percent: number;
  core_target_ratio?: number;
  rotation_target_ratio?: number;
  basis: 'clothing_target_pieces';
  clothing_capacity: number;
  core_target_pieces: number;
  rotation_target_pieces: number;
  core_actual_pieces: number;
  rotation_actual_pieces: number;
  observation_actual_pieces?: number;
  noncore_actual_pieces?: number;
  unfilled_pieces: number;
  notes: string[];
}

export interface TrialCandidate extends RackItem {
  cat: string;
  price: number;
  recommended_rack_id: string | null;
  selected: boolean;
  new_trial?: boolean;
  selection_reason: string;
  availability: 'ready' | 'blocked';
  rank: number;
}

export interface Rack {
  id: string;
  label: string;
  capacity: number;
  theme: string;
  items: RackItem[];
}

export interface FixtureSetting {
  id: string;
  label: string;
  capacity_pieces: number;
  theme: string;
  allowed_categories: string[];
}

export interface OutletCandidate {
  outlet_item_id: number;
  sku: string;
  title: string;
  color: string | null;
  size: string | null;
  available: number;
  price: number;
  outlet_price?: number;
  suggested_qty?: number;
  selected_for_plan?: boolean;
  image_url: string | null;
  variant_id: number | null;
  reason: string;
  requires_confirmation: boolean;
}

export interface MerchandisingPlan {
  autumn_transition?: AutumnTransition;
  planning_horizon?: PlanningHorizon;
  proposed_worklist?: ProposedWorklist;
  launch_candidates?: LaunchCandidate[];
  launch_reviews?: LaunchReview[];
  strategy?: AssortmentStrategy;
  schema_ready: boolean;
  active_plan: { id: number; created_at: string; review_due_at: string } | null;
  racks: Rack[];
  proposed_racks?: Rack[];
  trial_candidates?: TrialCandidate[];
  stock_rotation_candidates?: TrialCandidate[];
  trial_limit?: number;
  settings?: { version: number; fixtures: FixtureSetting[] };
  outlet_candidates: OutletCandidate[];
  feedback: unknown[];
  replacement_reviews?: { sku: string; rack_id: string; action: string; replacement_sku: string | null; replacement_title: string | null; reason: string; requires_confirmation: boolean }[];
}

export interface SavedMerchandisingPlan {
  id: number;
  created_at: string;
  review_due_at: string;
  active: boolean;
  label: string;
  date_local: string;
  timezone: 'Asia/Taipei';
  sku_count?: number;
  piece_count?: number;
  payload?: { racks: Rack[]; generated_at?: string; evidence_snapshot?: unknown; planning_horizon?: PlanningHorizon; launch_reviews?: LaunchReview[] };
}

export function normalizeMerchandising(data: MerchandisingPlan): MerchandisingPlan {
  const normalizeRacks = (racks: Rack[]): Rack[] => racks.map((rack) => ({ ...rack, items: rack.items.map((item) => ({
    ...item, variants: item.variants.map((variant) => ({ ...variant, variant_id: Number(variant.variant_id) })),
  })) }));
  return { ...data, racks: normalizeRacks(data.racks), proposed_racks: data.proposed_racks ? normalizeRacks(data.proposed_racks) : undefined,
    trial_candidates: data.trial_candidates?.map((item) => ({ ...item, variants: item.variants.map((variant) => ({ ...variant, variant_id: Number(variant.variant_id) })) })),
    stock_rotation_candidates: data.stock_rotation_candidates?.map((item) => ({ ...item, variants: item.variants.map((variant) => ({ ...variant, variant_id: Number(variant.variant_id) })) })),
    active_plan: data.active_plan ? { ...data.active_plan, id: Number(data.active_plan.id) } : null,
    outlet_candidates: data.outlet_candidates.map((item) => ({ ...item, outlet_item_id: Number(item.outlet_item_id), variant_id: item.variant_id == null ? null : Number(item.variant_id) })),
  };
}

export const rackPieces = (rack: Rack): number => rack.items.reduce((sum, item) => sum + item.variants.reduce((qty, variant) => qty + variant.target, 0), 0);

export function newReference(prefix: string): string {
  return `${prefix}-${typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
}
